/** @format */

/**
 * 会话分析 — 单会话详情：描述信息 + RootSpans 时间轴（GetAppMonitorSessionDetail）。
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { _$, Card, Icon } from '@king-design/react'
import { useLocation, useHistory } from 'react-router'
import { Descriptions, DescriptionItem, FormatCompactK, MonitorCrumb } from 'src/components'
import { useAsync } from 'react-use'
import Timeline from './Timeline'
import SpanItem from './SpanItem'
import type { SessionDetail } from './typescript'
import { formatDetailTime } from 'src/utils'
import { getSessionDetail } from 'src/api/ApiAIObservability'
import { durationFormatCompact } from 'src/utils/common'
import variable from 'src/static/styles/var'

/** 解析当前 URL search，供详情请求参数使用 */
function useQuery() {
  return new URLSearchParams(useLocation().search)
}

const SessionDetailPage: React.FC<{ className?: string }> = ({ className }) => {
  const history = useHistory()
  const location = useLocation()
  const query = useQuery()
  const serviceName = query.get('serviceName')
  const sessionId = query.get('sessionId')

  const [expand, setExpand] = useState<{ [key: string]: boolean }>({})
  /** 防止从 Trace 返回后立即 click-through 触发第二次 goBack */
  const [backReady, setBackReady] = useState(false)
  const backLockRef = useRef(false)
  const [activeTraceId, setActiveTraceId] = useState<string>()
  const traceListRef = useRef<HTMLDivElement>(null)

  /** 时间轴点击：高亮对应链路并滚动到 SpanItem */
  const scrollToTraceItem = useCallback((traceId: string) => {
    if (!traceId) {
      return
    }
    setActiveTraceId(prev => (prev === traceId ? undefined : traceId))
    setExpand(prev => {
      if (prev[traceId]) {
        return prev
      }
      return { ...prev, [traceId]: true }
    })
    requestAnimationFrame(() => {
      const el = traceListRef.current?.querySelector<HTMLElement>(`[data-trace-id="${traceId}"]`)
      el?.scrollIntoView({ behavior: 'auto', block: 'start' })
    })
  }, [])

  const toggleSpanExpand = useCallback((traceId: string) => {
    setExpand(prev => ({
      ...prev,
      [traceId]: !prev[traceId]
    }))
  }, [])

  const { value: sessionDetail } = useAsync(async (): Promise<SessionDetail | undefined> => {
    if (!serviceName || !sessionId) {
      return undefined
    }
    const result = await getSessionDetail({
      ServiceName: serviceName,
      SessionId: sessionId
    })
    return result?.SessionId ? (result as SessionDetail) : undefined
  }, [serviceName, sessionId])

  useEffect(() => {
    setBackReady(false)
    const timer = window.setTimeout(() => setBackReady(true), 200)
    return () => window.clearTimeout(timer)
  }, [location.pathname, location.search])

  useEffect(() => {
    if (!sessionDetail?.RootSpans?.length) {
      return
    }
    const expandMap = sessionDetail.RootSpans.reduce((acc, trace) => {
      acc[trace.TraceId] = true
      return acc
    }, {} as { [key: string]: boolean })
    setExpand(expandMap)
  }, [sessionDetail?.SessionId])

  const handleBack = useCallback(() => {
    if (!backReady || backLockRef.current) {
      return
    }
    backLockRef.current = true
    window.setTimeout(() => {
      backLockRef.current = false
    }, 350)
    history.go(-1)
  }, [backReady, history])

  // 跳转Trace详情（携带 from 供 Trace 详情页显式返回，避免 goBack 受历史栈污染影响）
  const toTraceDetail = (row: { TraceId: string; StartTime: string }) => {
    const qs = new URLSearchParams({
      from: 'session',
      traceId: row.TraceId,
      sessionId: sessionId || ''
    })
    const hashPath = `/monitor/aiobservability/traceanalysis/detail?${qs.toString()}`
    history.push(hashPath)
  }

  const errorCount = Number(sessionDetail?.ErrorCount)
  const hasErrorCount = !Number.isNaN(errorCount) && errorCount > 0

  return (
    <div className={className}>
      <MonitorCrumb
        breadArr={[
          { title: _$('AI可观测') },
          { title: _$('会话分析'), route: '/monitor/aiobservability/sessionanalysis' },
          { title: sessionId ?? '-' }
        ]}
      />
      <Card
        className="session-analysis-card"
        slotTitle={
          <div className="k-card-title-back" onClick={handleBack}>
            <Icon className="ion-ios-arrow-back" />
            <span>{_$('会话详情')}</span>
          </div>
        }
      >
        <div className="session-detail-desc">
          <Descriptions columns={3}>
            <DescriptionItem label={_$('会话ID')}>
              {sessionDetail?.SessionId ?? '-'}
            </DescriptionItem>
            <DescriptionItem label={_$('服务名称')}>
              {sessionDetail?.ServiceName ?? '-'}
            </DescriptionItem>
            <DescriptionItem label={_$('耗时')}>
              {sessionDetail?.DurationMs != null
                ? durationFormatCompact(sessionDetail.DurationMs)
                : '-'}
            </DescriptionItem>
            <DescriptionItem label={_$('会话开始时间')}>
              {formatDetailTime(sessionDetail?.StartTime)}
            </DescriptionItem>
            <DescriptionItem label={_$('会话结束时间')}>
              {formatDetailTime(sessionDetail?.EndTime)}
            </DescriptionItem>
            <DescriptionItem label={_$('LLM调用次数')}>
              {sessionDetail?.InvocationCount ?? '-'}
            </DescriptionItem>
            <DescriptionItem label={_$('错误次数')}>
              <span className={hasErrorCount ? 'session-detail-error-count' : undefined}>
                {sessionDetail?.ErrorCount ?? '-'}
              </span>
            </DescriptionItem>
            <DescriptionItem label={_$('链路数量')}>
              {sessionDetail?.TraceCount ?? '-'}
            </DescriptionItem>
            <DescriptionItem span={1} label={_$('Token使用情况')}>
              {sessionDetail ? (
                <div className="session-detail-token-wrap">
                  <div className="session-detail-token-inline">
                    <span className="session-detail-token-item">
                      {_$('Total Tokens')}：<FormatCompactK value={sessionDetail.TotalTokens} />
                    </span>
                    <span className="session-detail-token-sep" aria-hidden />
                    <span className="session-detail-token-item">
                      {_$('输入Token')}：<FormatCompactK value={sessionDetail.InputTokens} />
                    </span>
                    <span className="session-detail-token-sep" aria-hidden />
                    <span className="session-detail-token-item">
                      {_$('输出Token')}：<FormatCompactK value={sessionDetail.OutputTokens} />
                    </span>
                    <span className="session-detail-token-sep" aria-hidden />
                    <span className="session-detail-token-item">
                      {_$('缓存读取Token')}：
                      <FormatCompactK value={sessionDetail.CacheReadTokens} />
                    </span>
                  </div>
                </div>
              ) : (
                '-'
              )}
            </DescriptionItem>
          </Descriptions>
        </div>
        <Timeline
          spans={sessionDetail?.RootSpans ?? []}
          toTraceDetail={toTraceDetail}
          activeTraceId={activeTraceId}
          onTraceClick={scrollToTraceItem}
        />
        <div ref={traceListRef} className="session-detail-trace-list">
          {sessionDetail?.SessionTraces.map((trace, index) => (
            <SpanItem
              index={index}
              key={trace.TraceId}
              toTraceDetail={toTraceDetail}
              trace={trace}
              expand={expand[trace.TraceId]}
              active={activeTraceId === trace.TraceId}
              onExpandChange={() => toggleSpanExpand(trace.TraceId)}
            />
          ))}
        </div>
      </Card>
    </div>
  )
}

export default styled(SessionDetailPage)`
  padding: 0 16px;
  width: 100%;
  height: 100%;

  .session-detail-desc {
    max-width: 100%;
    box-sizing: border-box;

    .session-detail-error-count {
      color: ${variable.errorColor};
    }

    .session-detail-token-wrap {
      overflow-x: auto;
      overflow-y: hidden;
      word-break: normal;

      .session-detail-token-inline {
        display: inline-flex;
        flex-wrap: nowrap;
        align-items: center;
        white-space: nowrap;
        gap: 0;
        min-width: min-content;

        .session-detail-token-item {
          flex-shrink: 0;
          white-space: nowrap;
        }

        .session-detail-token-sep {
          flex-shrink: 0;
          width: 1px;
          height: 14px;
          margin: 0 16px;
          background: rgba(0, 0, 0, 0.15);
        }
      }
    }
  }
`
